import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Collapse,
  FormControl,
  FormControlLabel,
  OutlinedInput,
  Popover,
  Radio,
  RadioGroup,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC, useCallback, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { MdExpandMore as ExpandMore } from 'react-icons/md';
import { important } from 'common-types';
import { CodeRepos } from '../onboarding-types';
import AdvancedOptionsInstructions from './AdvancedOptionsInstructions';

const AdvancedOptions: FC<React.PropsWithChildren<AdvancedOptionsProps>> = ({
  open,
  handleOpenClose,
  credentialsType,
  errorMessage,
  error,
  onOptionChange,
  hostUrl,
  tokenOrPasswordValue,
  onInputChange,
  disabled,
  usernameValue,
  chosenRepoDisplayName,
  chosenRepoName,
}) => {
  const { classes } = useStyles();

  const [selectedOption, setSelectedOption] = useState<RadioButtonValues>(
    RadioButtonValues.IDP,
  );

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onOptionChange(event.target.value === RadioButtonValues.ManualCreds);
      setSelectedOption(event.target.value as RadioButtonValues);
    },
    [onOptionChange],
  );

  const showAdvancedOptionsInstructions = () => {
    setPopoverAnchor(document.getElementById('onboarding-modal'));
  };

  const hideAdvancedOptionsInstructions = () => {
    setPopoverAnchor(null);
  };

  const instructionsOpen = Boolean(popoverAnchor);

  return (
    <div className={classes.advancedOptionsForm}>
      <Tooltip
        arrow
        placement='top'
        title={disabled ? 'Please choose a code repository' : ''}>
        <Accordion
          disabled={disabled}
          classes={{ disabled: classes.disabled }}
          disableGutters
          expanded={open}
          onChange={handleOpenClose}
          elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMore />}
            aria-controls='panel1a-content'
            classes={{ content: classes.accordionSummaryContent }}
            id='panel1a-header'>
            Other Authorization Options
          </AccordionSummary>
          <AccordionDetails>
            <div>
              <FormControl>
                <RadioGroup value={selectedOption} onChange={handleChange}>
                  <FormControlLabel
                    value={RadioButtonValues.IDP}
                    control={<Radio color='primary' size='small' />}
                    label={
                      <Typography variant='body1' color='primary'>
                        Use git identity provider
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value={RadioButtonValues.ManualCreds}
                    control={<Radio color='primary' size='small' />}
                    label={
                      <Typography variant='body1' color='primary'>
                        Use git access token
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>
              <Collapse
                onEntered={showAdvancedOptionsInstructions}
                onExiting={hideAdvancedOptionsInstructions}
                in={selectedOption === RadioButtonValues.ManualCreds}>
                <div>
                  <FormControl className={classes.formControl} fullWidth>
                    <OutlinedInput
                      value={hostUrl}
                      onChange={onInputChange}
                      name='hostURL'
                      placeholder='Git Host URL'
                      size='small'
                      fullWidth
                    />
                  </FormControl>
                  {credentialsType ===
                    AdvancedOptionsCredTypes.UserAndPassword && (
                    <FormControl className={classes.formControl} fullWidth>
                      <OutlinedInput
                        value={usernameValue}
                        onChange={onInputChange}
                        name='username'
                        placeholder='Username'
                        size='small'
                        fullWidth
                      />
                    </FormControl>
                  )}
                  <FormControl className={classes.formControl} fullWidth>
                    <OutlinedInput
                      value={tokenOrPasswordValue}
                      onChange={onInputChange}
                      name='token'
                      placeholder={
                        credentialsType ===
                        AdvancedOptionsCredTypes.UserAndPassword
                          ? 'Password'
                          : 'Token'
                      }
                      type={
                        credentialsType ===
                        AdvancedOptionsCredTypes.UserAndPassword
                          ? 'password'
                          : 'text'
                      }
                      size='small'
                      fullWidth
                      error={!!error}
                    />
                  </FormControl>
                </div>
              </Collapse>
            </div>
            {errorMessage && (
              <Typography color='error' variant='body2' mt={1}>
                {errorMessage}
              </Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Tooltip>
      <Popover
        classes={{ root: classes.popover, paper: classes.popoverPaper }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        hideBackdrop
        disableEnforceFocus
        anchorEl={popoverAnchor}
        open={instructionsOpen}>
        {chosenRepoDisplayName && chosenRepoName && (
          <AdvancedOptionsInstructions
            repoType={chosenRepoName}
            repoDisplayName={chosenRepoDisplayName}
          />
        )}
      </Popover>
    </div>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  advancedOptionsForm: {
    display: 'flex',
    flexDirection: 'column',
    paddingBlockStart: theme.spacing(1.5),
    textAlign: 'start',
  },
  formControl: {
    marginBlockStart: `${theme.spacing(1)} !important`,
  },
  accordionSummaryContent: { flexGrow: important('0'), textAlign: 'center' },
  popover: {
    pointerEvents: 'none',
  },
  popoverPaper: {
    backgroundColor: important('transparent'),
  },
  disabled: {
    opacity: important('1'),
  },
}));

interface AdvancedOptionsProps {
  open: boolean;
  handleOpenClose: () => void;
  credentialsType: AdvancedOptionsCredTypes;
  error?: boolean;
  errorMessage?: string;
  onOptionChange: (bool: boolean) => void;
  hostUrl: string;
  tokenOrPasswordValue: string;
  usernameValue: string;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  chosenRepoDisplayName?: string;
  chosenRepoName?: CodeRepos;
}

export enum AdvancedOptionsCredTypes {
  Token = 'Token',
  UserAndPassword = 'UserAndPassword',
}

enum RadioButtonValues {
  IDP = 'IDP',
  ManualCreds = 'Manual',
}

export default AdvancedOptions;
